import React, { PropsWithChildren, createContext, useCallback, useEffect, useState } from 'react';
import { uploadQueue, downloadCatalog, type UploadQueueResult, type DownloadCatalogResult } from '../lib/sync-manager';
import { useSnackbar } from '../components/SnackbarProvider';

/**
 * Sync status type
 */
export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

/**
 * Sync result type
 */
export interface SyncResult {
  uploadResult?: UploadQueueResult;
  downloadResult?: DownloadCatalogResult;
  error?: string;
}

/**
 * Context type for sync functionality
 */
type SyncContextType = {
  /** Current sync status */
  isSyncing: boolean;
  /** Sync status string */
  syncStatus: SyncStatus;
  /** Last sync result */
  lastSyncResult: SyncResult | null;
  /** Last successful sync timestamp */
  lastSyncAt: string | null;
  /** Trigger a manual sync */
  triggerSync: (locationId?: string) => Promise<SyncResult>;
};

/**
 * Props for SyncContextProvider
 */
interface SyncContextProviderProps extends PropsWithChildren {
  /** Sync interval in milliseconds (default: 5 minutes) */
  syncIntervalMs?: number;
}

export const SyncContext = createContext<SyncContextType>({} as SyncContextType);

/**
 * Provider component for sync functionality
 *
 * Provides sync state management and manual sync triggering to the application.
 * Manages sync status, tracks last sync results, and provides a triggerSync method
 * that uploads queued transactions and downloads the product catalog.
 * Automatically syncs periodically when online and when connection is restored.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <SyncContextProvider syncIntervalMs={300000}>
 *       <YourApp />
 *     </SyncContextProvider>
 *   );
 * }
 *
 * // In child components:
 * const { isSyncing, triggerSync } = useContext(SyncContext);
 * ```
 */
export const SyncContextProvider: React.FC<SyncContextProviderProps> = ({
  children,
  syncIntervalMs = 5 * 60 * 1000, // Default: 5 minutes
}) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const { showMessage } = useSnackbar();

  /**
   * Trigger a manual sync
   * Uploads queued transactions and downloads product catalog
   */
  const triggerSync = useCallback(async (locationId?: string): Promise<SyncResult> => {
    // Don't start a new sync if one is already in progress
    if (syncStatus === 'syncing') {
      throw new Error('Sync already in progress');
    }

    setSyncStatus('syncing');
    const result: SyncResult = {};

    try {
      // Step 1: Upload queued sales
      try {
        const uploadResult = await uploadQueue(locationId);
        result.uploadResult = uploadResult;
      } catch (uploadError) {
        // Continue with download even if upload fails
        result.error = uploadError instanceof Error ? uploadError.message : 'Upload failed';
      }

      // Step 2: Download product catalog
      try {
        const downloadResult = await downloadCatalog(locationId);
        result.downloadResult = downloadResult;
      } catch (downloadError) {
        // Append download error to any existing error
        const downloadMsg = downloadError instanceof Error ? downloadError.message : 'Download failed';
        result.error = result.error ? `${result.error}; ${downloadMsg}` : downloadMsg;
      }

      // Update status based on results
      if (result.error) {
        setSyncStatus('error');
        showMessage(`Sync failed: ${result.error}`, 'error');
      } else {
        setSyncStatus('success');
        setLastSyncAt(new Date().toISOString());

        // Check for partial failures (conflicts)
        const hasFailures = result.uploadResult && result.uploadResult.failureCount > 0;
        if (hasFailures) {
          const { successCount, failureCount } = result.uploadResult!;
          showMessage(
            `Sync completed with conflicts: ${successCount} succeeded, ${failureCount} failed`,
            'warning'
          );
        } else {
          // Calculate total items synced
          const uploadedCount = result.uploadResult?.successCount || 0;
          const downloadedCount = (result.downloadResult?.productsCount || 0) + (result.downloadResult?.inventoryCount || 0);

          if (uploadedCount > 0 || downloadedCount > 0) {
            showMessage(
              `Sync successful: ${uploadedCount} sales uploaded, ${downloadedCount} items downloaded`,
              'success'
            );
          } else {
            showMessage('Sync completed - all data is up to date', 'success');
          }
        }
      }

      setLastSyncResult(result);
      return result;
    } catch (error) {
      // Catch any unexpected errors
      const errorMsg = error instanceof Error ? error.message : 'Sync failed';
      const errorResult: SyncResult = { error: errorMsg };

      setSyncStatus('error');
      setLastSyncResult(errorResult);
      showMessage(`Sync failed: ${errorMsg}`, 'error');

      return errorResult;
    }
  }, [syncStatus, showMessage]);

  /**
   * Automatically sync when connection is restored
   */
  useEffect(() => {
    const handleOnline = () => {
      // Trigger sync when device comes back online
      triggerSync().catch((error) => {
        // Silently handle error - sync status will be updated
      });
    };

    // Add event listener
    window.addEventListener('online', handleOnline);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [triggerSync]);

  /**
   * Periodic background sync when online
   */
  useEffect(() => {
    // Only set up periodic sync if online
    if (!navigator.onLine) {
      return;
    }

    // Set up interval for periodic sync
    const intervalId = setInterval(() => {
      // Check if online before each sync
      if (navigator.onLine) {
        triggerSync().catch((error) => {
          // Silently handle error - sync status will be updated
        });
      }
    }, syncIntervalMs);

    // Cleanup interval on unmount or when syncIntervalMs changes
    return () => {
      clearInterval(intervalId);
    };
  }, [syncIntervalMs, triggerSync]);

  return (
    <SyncContext.Provider
      value={{
        isSyncing: syncStatus === 'syncing',
        syncStatus,
        lastSyncResult,
        lastSyncAt,
        triggerSync,
      }}
    >
      {children}
    </SyncContext.Provider>
  );
};
